/* eslint-disable react/react-in-jsx-scope */
import "./assets/global.scss";
import {
  Navigate,
  useNavigate,
  useLocation,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import Activities from "./pages/Activities";
import Auditlog from "./pages/AuditLog/AuditLog";
import Favorite from "./pages/Favorite";
import Footer from "./layouts/footer/Footer";
import Header from "./layouts/header/Header";
import Home from "./pages/home/Home";
import PageDetail from "./layouts/page-detail/PageDetail";
import Shared from "./pages/Shared";
import Sidenav from "./layouts/sidenav/Sidenav";
import SignIn from "./pages/signin/SignIn";
import { ThemeProvider } from "@mui/material";
import theme from "./assets/theme/theme";
import AlertBox from "./components/ui/AlertBox";
import { useEffect, useState } from "react";
import ErrorBoundary from "./components/ErrorHandling/ErrorBoundary";
import UiComponent from "./pages/Ui-component";
import Manage from "./pages/Admin/Manage/Manage";
import AuditTraceProvider from "./components/form/AuditTraceProvider";
import PrivateRoute from "./components/authentication/PrivateRoute";
import {
  GLOBALSETTINGS,
  IMPERSONATION_INFO,
  LOCALSTORAGENAME,
  ModuleNames,
  SEVERITIES,
  USERINFO,
} from "./data/constants";
import Recycle from "./features/recycle/Recycle";
import ErrorLog from "./pages/Admin/ErrorLog/ErrorLog";
import { logPortalErrorGlobal, isBoundaryHandled } from "./utils/errorLogger";
import { useUser } from "./context/UserContext";
import ErrorFallBackUIRoute from "./components/ErrorHandling/ErrorFallBackUIRoute";
import { appInsights } from "./utils/AppInsights";
import PageNotFound from "./pages/error/PageNotFound";
import { ERRORMESSAGES, GLOBALMESSAGES, SETTING_CHANGED } from "./constants/messages";
import { isNetworkFailure } from "./utils/userFacingError";
import { store } from "./redux/store";
import { showAlert } from "./redux/alertSlice";
import ListDetails from "./pages/home/ListDetails";
import ScrollToTop from "./components/form/ScrollToTop";
import { MenuProvider, useMenu } from "./context/MenuContext";
import MenuGuard from "./components/form/MenuGuard";
import NotAuthorized from "./pages/error/NotAuthorized";
import MessageTabs from "./features/messages/internalMessages/MessageTabs";
import { isTokenValid } from "./utils/commonHelper";
import { clearLocalStorageKeepingPreferences } from "./utils/localStoragePreferences";
import UserProfile from "./pages/UserProfile/UserProfile";

import SsoCallback from "./pages/signin/SSOCallback";
import FolderLayout from "./pages/home/FolderLayout";
import { DownloadProvider } from "./features/download/DownloadContext";
import SessionWatcher from "./pages/Admin/SessionLockUnlock/SessionWatcher";
import { SignalRProvider } from "./context/SignalRProvider";
import ManageSettings from "./pages/Admin/ManageSettings/ManageSettings";
import { LicenseInfo } from "@mui/x-license";
import { useSettingsHub } from "./hooks/useSettingsHub";
import { useRefreshSettings } from "./hooks/useRefreshSettings";
import { useToaster } from "./hooks/useToaster";
import ContactPage from "./pages/contacts/ContactPage";
import { getPortalSecretValue } from "./services/keyVaultService";
import { useSettingValue } from "./services/useSettingValue";
import { SYSTEMSETTINGS } from "./data/constants";
import WhiteListedIpList from "./pages/Admin/WhiteListedIpList/WhiteListedIpList";
import PostInterviewForm from "./pages/supervision/PostInterviewForm/PostInterviewForm";
import { hamaspikTheme } from "./assets/hamaspik/theme";
import PostInterviewForms from "./features/supervision/PostInterviewForms";
import PostIFApproval from "./pages/supervision/PostInterviewForm/approval/PostIFApproval";
import WorkflowLibrary from "./pages/workflows/WorkflowLibrary/WorkflowLibrary";
import WorkflowBuilder from "./pages/workflows/WorkflowBuilder/WorkflowBuilder";
import WorkflowRun from "./pages/workflows/WorkflowRun/WorkflowRun";
import WorkflowShareLink from "./pages/workflows/WorkflowRun/WorkflowShareLink";
import WorkflowInstances from "./pages/workflows/WorkflowInstances/WorkflowInstances";
//Please keep this line at the end og all import
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);

const RootRedirect = () => {
  const { userId } = useUser();
  return userId && userId > 0 && isTokenValid() ? (
    <Navigate to={GLOBALSETTINGS.DEFAULT_PAGE} />
  ) : (
    <SignIn />
  );
};

const Layout = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const sessionValue = localStorage.getItem(USERINFO.SESSIONVALUE);
  const navigate = useNavigate();
  const location = useLocation();
  // These feature screens render their own page headers, so the shared page-detail band is skipped.
  const pathName = location.pathname.toLowerCase();
  const hidePageDetail =
    pathName.startsWith("/hiring/postif") || pathName.startsWith("/workflow");
  const showToaster = useToaster();
  const { permissions } = useMenu();
  const refreshSettings = useRefreshSettings({ silent: true });

  const userActivityDuration = useSettingValue(SYSTEMSETTINGS.USER_ACTIVITY_DURATION);

  const inactivityMinutes =
    userActivityDuration !== null && userActivityDuration !== undefined
      ? Number(userActivityDuration)
      : null;
  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 1024);
  };

  useSettingsHub((data) => {
    refreshSettings();
    const canViewManageSetting = permissions?.find(
      (permission) => permission.moduleName === ModuleNames.ManageSettings,
    )?.canView;
    if (canViewManageSetting) {
      showToaster(SETTING_CHANGED(data.settingName, data.updatedBy), SEVERITIES.INFO);
    }
  });

  useEffect(() => {
    async function initLicense() {
      try {
        const secrets = await getPortalSecretValue();
        if (secrets.MUILicenseKey) {
          LicenseInfo.setLicenseKey(secrets.MUILicenseKey);
        } else {
          console.warn("MUI License key not found in secrets.");
        }
      } catch (error) {
        console.error("Failed to load secrets:", error);
      }
    }

    initLicense();
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Surface a toast stashed before a full reload (e.g. after ending impersonation),
  // since a reload clears the Redux-backed alert state.
  useEffect(() => {
    const pendingToast = localStorage.getItem(LOCALSTORAGENAME.PENDINGTOAST);
    if (pendingToast) {
      localStorage.removeItem(LOCALSTORAGENAME.PENDINGTOAST);
      showToaster(pendingToast, SEVERITIES.SUCCESS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LOCALSTORAGENAME.LOGOUTEVENT) {
        clearLocalStorageKeepingPreferences();
        navigate("/signin");
        return;
      }

      if (
        event.key === IMPERSONATION_INFO.IsImpersonating &&
        (event.newValue === null || event.newValue === "false")
      ) {
        window.location.href = GLOBALSETTINGS.DEFAULT_PAGE;
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <div className="main">
          <Sidenav isOpen={isDrawerOpen} onClose={toggleDrawer} isMobile={isMobile} />
          <Header onMenuClick={toggleDrawer} isMobile={isMobile} isOpen={isDrawerOpen} />
          <div
            className={`main-content ${isMobile ? "main-content-small" : "main-content-big"} ${isDrawerOpen ? "open" : ""}`}
          >
            <DownloadProvider>
              {!hidePageDetail && <PageDetail />}
              <div className={`page-content ${hidePageDetail ? "page-content-blank" : ""}`}>
                <AlertBox />
                <Outlet />
              </div>
              <Footer />
            </DownloadProvider>
          </div>
        </div>
        {sessionValue && inactivityMinutes && (
          <SessionWatcher inactivityMinutes={inactivityMinutes} />
        )}
      </ThemeProvider>
    </ErrorBoundary>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/signin",
    element: <RootRedirect />,
  },
  {
    element: <ContactPage />,
    path: "/contactpage",
  },
  { element: <SsoCallback />, path: "/SsoCallback" },
  { element: <PageNotFound />, path: "/error" },
  { element: <ErrorFallBackUIRoute />, path: "/error-page" },
  {
    element: (
      <PrivateRoute>
        <MenuProvider>
          <AuditTraceProvider>
            <ScrollToTop>
              <Layout />
            </ScrollToTop>
          </AuditTraceProvider>
        </MenuProvider>
      </PrivateRoute>
    ),
    path: "/",
    children: [
      {
        element: (
          <MenuGuard>
            <FolderLayout />
          </MenuGuard>
        ),
        children: [
          {
            element: (
              <MenuGuard>
                <Home />
              </MenuGuard>
            ),
            path: "/Home",
          },
          {
            element: (
              <MenuGuard>
                <ListDetails />
              </MenuGuard>
            ),
            path: "/listDetails",
          },
        ],
      },
      {
        element: (
          <MenuGuard>
            <Activities />
          </MenuGuard>
        ),
        path: "/Activities",
      },
      {
        element: <Favorite />,
        path: "/Favorite",
      },
      {
        element: (
          <MenuGuard>
            <Recycle />
          </MenuGuard>
        ),
        path: "/Recyclebin",
      },
      {
        element: <Shared />,
        path: "/Shared",
      },
      {
        path: "/audit-log",
        element: (
          <MenuGuard>
            <Auditlog />
          </MenuGuard>
        ),
      },
      {
        element: (
          <MenuGuard>
            <ErrorLog />
          </MenuGuard>
        ),
        path: "/error-log",
      },
      {
        element: (
          <MenuGuard>
            <Manage />
          </MenuGuard>
        ),
        path: "/Manage",
      },
      {
        element: <ContactPage />,
        path: "/contacts",
      },
      {
        element: <UiComponent />,
        path: "/ui-component",
      },
      {
        element: (
          <MenuGuard>
            <MessageTabs />
          </MenuGuard>
        ),
        path: "/messages",
      },
      {
        element: (
          <MenuGuard>
            <ManageSettings />
          </MenuGuard>
        ),
        path: "/manageSettings",
      },
      {
        element: (
          <MenuGuard>
            <WhiteListedIpList />
          </MenuGuard>
        ),
        path: "/white-listed-ip",
      },
      {
        element: <UserProfile />,
        path: "/user-profile",
      },
      {
        element: (
          <MenuGuard>
            <PostInterviewForms />
          </MenuGuard>
        ),
        path: "/hiring/postif",
      },
      {
        element: (
          <MenuGuard>
            <WorkflowLibrary />
          </MenuGuard>
        ),
        path: "/workflow/library",
      },
      {
        element: (
          <MenuGuard>
            <WorkflowBuilder />
          </MenuGuard>
        ),
        path: "/workflow/builder",
      },
      {
        element: (
          <MenuGuard>
            <WorkflowRun />
          </MenuGuard>
        ),
        path: "/workflow/run",
      },
      {
        element: (
          <MenuGuard>
            <WorkflowInstances />
          </MenuGuard>
        ),
        path: "/workflow/instances",
      },
      // Epic 80431: the seeded My Work PageUrl renders the same shared screen with the My Work
      // panel in focus, rather than redirecting away to /workflow/instances.
      {
        element: (
          <MenuGuard>
            <WorkflowInstances />
          </MenuGuard>
        ),
        path: "/workflow/my-work",
      },
      // Epic 80430 (decision 6.5): the case share link. It redirects to /workflow/run, which is guarded,
      // so it is deep-link only â€” no sidebar entry, and no System_Modules / Portal DB rows needed.
      {
        element: <WorkflowShareLink />,
        path: "/wf/:refNo",
      },
      {
        element: <Navigate to="/hiring/postif" replace />,
        path: "/hiring/postif/list",
      },
      // The form and approval screens keep their own theme and page header, but render inside
      // the shell's content area like every other page. Gated on EnterprisePortal-PostIF-Form
      // via RouteKeywords ("postif/" â†’ ModuleNames.PostIFForm).
      {
        element: (
          <MenuGuard>
            <ErrorBoundary>
              <ThemeProvider theme={hamaspikTheme}>
                <PostInterviewForm />
              </ThemeProvider>
            </ErrorBoundary>
          </MenuGuard>
        ),
        path: "/hiring/postif/new",
      },
      {
        element: (
          <MenuGuard>
            <ErrorBoundary>
              <ThemeProvider theme={hamaspikTheme}>
                <PostInterviewForm />
              </ThemeProvider>
            </ErrorBoundary>
          </MenuGuard>
        ),
        path: "/hiring/postif/:id/edit",
      },
      {
        // Opened by record id from the list. The same screen serves the emailed approver at
        // /sign/:token below; it renders read-only unless the viewer is the current signer.
        // Deliberately NOT gated by MenuGuard/EnterprisePortal-PostIF-Form: that module is the
        // hiring supervisor's *create* permission, which real approvers (Dept/HR/Exec Director)
        // and HR reviewers (who only hold PostIFList) may lack. Authorization is enforced
        // server-side â€” postif/{id} runs CanAct('open') and returns 404 when denied (creator,
        // HR, reports-to, or the current pending signer), so no data is exposed by dropping the
        // module gate. A portal login (PrivateRoute, from the parent route) is still required.
        // Matches /sign/:token below, which was never gated for the same reason.
        element: (
          <ErrorBoundary>
            <ThemeProvider theme={hamaspikTheme}>
              <PostIFApproval />
            </ThemeProvider>
          </ErrorBoundary>
        ),
        path: "/hiring/postif/:id",
      },
      {
        element: (
          <ErrorBoundary>
            <ThemeProvider theme={hamaspikTheme}>
              <PostIFApproval />
            </ThemeProvider>
          </ErrorBoundary>
        ),
        path: "/hiring/postif/sign/:token",
      },
    ],
  },
  { element: <PageNotFound />, path: "*" },
  {
    element: <NotAuthorized isAccessMenu={true} />,
    path: "/not-authorized",
  },
]);

function App() {
  const { userId } = useUser();
  const sessionValue = localStorage.getItem(USERINFO.SESSIONVALUE);

  window.onunhandledrejection = async function (event: PromiseRejectionEvent) {
    if (isBoundaryHandled(event.reason)) {
      event.preventDefault();
      return;
    }
    // Bug 75680: a dropped connection rejects promises all over the app â€” every floating
    // service call and every retry â€” and navigating to /error-page turned each one into
    // "Something went wrong", overwriting the network message the user had just been given.
    // Stay on the page and repeat the network wording instead, so the session keeps reading
    // as a connection problem until connectivity is back.
    if (isNetworkFailure(event.reason)) {
      event.preventDefault();
      store.dispatch(
        showAlert({ message: GLOBALMESSAGES.NETWORK_ERROR, severity: SEVERITIES.ERROR })
      );
      // Not logPortalErrorGlobal â€” that POST cannot reach the server while offline. App
      // Insights buffers locally and flushes once the connection returns, so the failure
      // is still recorded rather than silently dropped.
      appInsights?.trackException({
        exception:
          event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason ?? "Unhandled Rejection")),
      });
      return;
    }
    try {
      const error = event.reason;
      await logPortalErrorGlobal({
        errorSummary: error?.message ?? "Unhandled Rejection",
        errorDetails: error?.stack ?? "",
        userId,
      });
      router.navigate("/error-page");
    } catch (err) {
      appInsights?.trackException({
        exception: new Error(ERRORMESSAGES.UNHANDLEDEXCEPTION, err),
      });
    }
  };

  return (
    <SignalRProvider userId={userId} sessionValue={sessionValue}>
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
        }}
      />
    </SignalRProvider>
  );
}

export default App;

